/**
 * プロフィール選択サブステップ（好きな色・得意な教科・容姿）
 */
import { useRef, useState } from "react";
import { usePlaySound } from "common/utils";
import { Select, SelectOption } from "common/elements/Select/Select";
import I18n from "features/parent/i18n";
import { COLOR_PALETTE, generateColorVariations, BaseColorKey } from "features/user/constants";
import {
  CreatePlayerState,
  PlayerFavoritesSubStep,
  AccessoryType,
  HairstyleType,
  BodyTypeType,
  FaceShapeType,
} from "../../types";

interface FavoritesStepProps {
  state: CreatePlayerState;
  updateState: (updates: Partial<CreatePlayerState>) => void;
  favoritesSubStep: PlayerFavoritesSubStep;
}

/** 得意な教科選択肢 */
const BEST_SUBJECT_OPTIONS = [
  { key: "japanese", icon: "menu_book", labelKey: "MSG_SUBJECT_LANGUAGE_BY_PARENT" },
  { key: "math", icon: "calculate", labelKey: "MSG_SUBJECT_MATH_BY_PARENT" },
  { key: "science", icon: "science", labelKey: "MSG_SUBJECT_SCIENCE_BY_PARENT" },
  { key: "social", icon: "public", labelKey: "MSG_SUBJECT_SOCIAL_BY_PARENT" },
  { key: "music", icon: "music_note", labelKey: "MSG_SUBJECT_MUSIC_BY_PARENT" },
  { key: "art", icon: "palette", labelKey: "MSG_SUBJECT_ART_BY_PARENT" },
  { key: "pe", icon: "sports_soccer", labelKey: "MSG_SUBJECT_PE_BY_PARENT" },
  { key: "life", icon: "eco", labelKey: "MSG_SUBJECT_LIFE_BY_PARENT" },
  { key: "home_ec", icon: "restaurant", labelKey: "MSG_SUBJECT_HOME_EC_BY_PARENT" },
  { key: "ethics", icon: "volunteer_activism", labelKey: "MSG_SUBJECT_ETHICS_BY_PARENT" },
  { key: "english", icon: "translate", labelKey: "MSG_SUBJECT_ENGLISH_BY_PARENT" },
  { key: "integrated", icon: "hub", labelKey: "MSG_SUBJECT_INTEGRATED_BY_PARENT" },
  {
    key: "special_activities",
    icon: "groups",
    labelKey: "MSG_SUBJECT_SPECIAL_ACTIVITIES_BY_PARENT",
  },
];

/** 身につけるもの選択肢 */
const ACCESSORY_OPTIONS: { key: AccessoryType; icon: string; labelKey: string }[] = [
  { key: "none", icon: "block", labelKey: "MSG_ACCESSORY_NONE_BY_PARENT" },
  { key: "megane", icon: "eyeglasses", labelKey: "MSG_ACCESSORY_MEGANE_BY_PARENT" },
  { key: "boushi", icon: "checkroom", labelKey: "MSG_ACCESSORY_BOUSHI_BY_PARENT" },
  { key: "hairpin", icon: "auto_fix_high", labelKey: "MSG_ACCESSORY_HAIRPIN_BY_PARENT" },
  { key: "ribbon", icon: "redeem", labelKey: "MSG_ACCESSORY_RIBBON_BY_PARENT" },
  { key: "bandaid", icon: "healing", labelKey: "MSG_ACCESSORY_BANDAID_BY_PARENT" },
];

/** 髪型選択肢 */
const HAIRSTYLE_OPTIONS: SelectOption<HairstyleType>[] = [
  { value: "very_short", label: "MSG_HAIRSTYLE_VERY_SHORT_BY_PARENT" },
  { value: "short", label: "MSG_HAIRSTYLE_SHORT_BY_PARENT" },
  { value: "medium", label: "MSG_HAIRSTYLE_MEDIUM_BY_PARENT" },
  { value: "slightly_long", label: "MSG_HAIRSTYLE_SLIGHTLY_LONG_BY_PARENT" },
  { value: "long", label: "MSG_HAIRSTYLE_LONG_BY_PARENT" },
];

/** 体型選択肢 */
const BODY_TYPE_OPTIONS: SelectOption<BodyTypeType>[] = [
  { value: "thin", label: "MSG_BODY_TYPE_THIN_BY_PARENT" },
  { value: "slightly_thin", label: "MSG_BODY_TYPE_SLIGHTLY_THIN_BY_PARENT" },
  { value: "medium", label: "MSG_BODY_TYPE_MEDIUM_BY_PARENT" },
  { value: "slightly_chubby", label: "MSG_BODY_TYPE_SLIGHTLY_CHUBBY_BY_PARENT" },
  { value: "chubby", label: "MSG_BODY_TYPE_CHUBBY_BY_PARENT" },
];

/** 顔の形選択肢 */
const FACE_SHAPE_OPTIONS: SelectOption<FaceShapeType>[] = [
  { value: "round", label: "MSG_FACE_SHAPE_ROUND_BY_PARENT" },
  { value: "oval", label: "MSG_FACE_SHAPE_OVAL_BY_PARENT" },
  { value: "oblong", label: "MSG_FACE_SHAPE_OBLONG_BY_PARENT" },
  { value: "square", label: "MSG_FACE_SHAPE_SQUARE_BY_PARENT" },
  { value: "triangle", label: "MSG_FACE_SHAPE_TRIANGLE_BY_PARENT" },
];

export const FavoritesStep = ({ state, updateState, favoritesSubStep }: FavoritesStepProps) => {
  const play = usePlaySound();

  // Select用のopen状態
  const [hairstyleOpen, setHairstyleOpen] = useState(false);
  const [bodyTypeOpen, setBodyTypeOpen] = useState(false);
  const [faceShapeOpen, setFaceShapeOpen] = useState(false);

  // Select用のanchorRef
  const hairstyleRef = useRef<HTMLDivElement>(null);
  const bodyTypeRef = useRef<HTMLDivElement>(null);
  const faceShapeRef = useRef<HTMLDivElement>(null);

  // ベース色選択ハンドラ（単一選択）
  const handleBaseColorSelect = (baseKey: BaseColorKey) => {
    play();
    updateState({
      selectedBaseColors: [baseKey],
      favoriteColors: [],
    });
  };

  // バリエーション選択ハンドラ（単一選択）
  const handleVariationSelect = (hexColor: string) => {
    play();
    updateState({ favoriteColors: [hexColor] });
  };

  const handleBestSubjectSelect = (subject: string) => {
    play();
    const current = state.bestSubjects || [];
    if (current.includes(subject)) {
      updateState({ bestSubjects: current.filter((s) => s !== subject) });
    } else {
      updateState({ bestSubjects: [...current, subject] });
    }
  };

  const handleAccessorySelect = (accessory: AccessoryType) => {
    play();
    const current = state.accessory || [];

    // 「なし」を選択した場合：他をすべて解除して「なし」のみにする
    if (accessory === "none") {
      if (current.includes("none")) {
        updateState({ accessory: [] });
      } else {
        updateState({ accessory: ["none"] });
      }
      return;
    }

    // 他のアクセサリーを選択した場合：「なし」を解除
    const withoutNone = current.filter((a) => a !== "none");
    if (withoutNone.includes(accessory)) {
      updateState({ accessory: withoutNone.filter((a) => a !== accessory) });
    } else {
      updateState({ accessory: [...withoutNone, accessory] });
    }
  };

  /** ラベル取得ヘルパー */
  const getOptionLabel = <T extends string>(
    options: SelectOption<T>[],
    value: T | null,
  ): string => {
    if (!value) return I18n.t("MSG_SELECT_PLACEHOLDER");
    const option = options.find((o) => o.value === value);
    return option ? I18n.t(option.label) : I18n.t("MSG_SELECT_PLACEHOLDER");
  };

  /** 色選択 */
  const renderColorSelection = () => {
    const selectedBase = (state.selectedBaseColors || [])[0] || null;
    const variations = selectedBase ? generateColorVariations(selectedBase) : [];

    return (
      <div className="flex-col-el items-center gap-6">
        {/* タイトル */}
        <p className="text-body text-textcolor-black text-[24px]">
          {I18n.t("MSG_PROFILE_AI_FAVORITE_COLOR_BY_PARENT")}
        </p>

        {/* 主色選択 */}
        <div className="flex-row-el flex-center gap-4">
          {COLOR_PALETTE.map((option) => {
            const isActive = selectedBase === option.key;
            const isWhite = option.key === "white";
            return (
              <div
                key={option.key}
                onClick={() => handleBaseColorSelect(option.key)}
                className="flex-col-el flex-center gap-2 cursor-pointer select-none"
              >
                <div
                  className={`w-[70px] h-[70px] rounded-full hover:opacity-90 ${isActive ? "ring-4 ring-green-100" : ""} ${isWhite ? "border-2 border-gray-40" : ""}`}
                  style={{ backgroundColor: option.hex }}
                >
                  {isActive && (
                    <div className="flex-col-el flex-center w-full h-full">
                      <span className="material-symbols-outlined text-[28px] text-white drop-shadow-md">
                        check
                      </span>
                    </div>
                  )}
                </div>
                <span
                  className={`text-body text-[14px] ${isActive ? "text-green-100" : "text-textcolor-black/70"}`}
                >
                  {I18n.t(`${option.labelKey}_BY_PARENT`)}
                </span>
              </div>
            );
          })}
        </div>

        {/* バリエーション選択 */}
        <div className="flex-col-el items-center gap-3">
          <p className="text-body text-textcolor-black text-[18px]">
            {I18n.t("MSG_COLOR_SELECT_SHADE_BY_PARENT")}
          </p>
          <div className="flex-row-el gap-4">
            {selectedBase
              ? variations.map((hex, idx) => {
                  const isSelected = (state.favoriteColors || []).includes(hex);
                  const isWhiteVariation = selectedBase === "white";
                  return (
                    <div
                      key={idx}
                      onClick={() => handleVariationSelect(hex)}
                      className={`flex-col-el flex-center w-[60px] h-[60px] rounded-full cursor-pointer select-none hover:opacity-90 ${isSelected ? "ring-4 ring-green-100" : ""} ${isWhiteVariation ? "border-2 border-gray-40" : ""}`}
                      style={{ backgroundColor: hex }}
                    >
                      {isSelected && (
                        <span className="material-symbols-outlined text-[24px] text-white drop-shadow-md">
                          check
                        </span>
                      )}
                    </div>
                  );
                })
              : Array.from({ length: 5 }).map((_, idx) => (
                  <div
                    key={idx}
                    className="flex-col-el flex-center w-[60px] h-[60px] rounded-full border-2 border-dashed border-gray-40"
                  />
                ))}
          </div>
        </div>
      </div>
    );
  };

  /** 得意な教科選択グリッド */
  const renderBestSubjectSelection = () => (
    <div className="flex-col-el items-center gap-6">
      {/* タイトル */}
      <p className="text-body text-textcolor-black text-[24px]">
        {I18n.t("MSG_PROFILE_AI_BEST_SUBJECTS_BY_PARENT")}
      </p>

      <div className="grid grid-cols-5 gap-3 justify-items-center">
        {BEST_SUBJECT_OPTIONS.map((option) => {
          const isActive = (state.bestSubjects || []).includes(option.key);
          return (
            <div
              key={option.key}
              onClick={() => handleBestSubjectSelect(option.key)}
              className={`flex-col-el flex-center relative w-[110px] h-[110px] rounded-[8px] cursor-pointer select-none hover:opacity-90 ${isActive ? "bg-green-alt border-2 border-green-100" : "bg-surface border-2 border-gray2-40"}`}
            >
              {isActive && (
                <span className="absolute top-1 right-1 material-symbols-outlined text-[14px] text-green-100">
                  check_circle
                </span>
              )}
              <span
                className={`material-symbols-outlined !text-[28px] mb-1 ${isActive ? "text-green-100" : "text-gray2-80"}`}
              >
                {option.icon}
              </span>
              <span
                className={`text-body text-[16px] text-center ${isActive ? "text-textcolor-black" : "text-textcolor-black/70"}`}
              >
                {I18n.t(option.labelKey)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );

  /** 容姿選択 */
  const renderAppearanceSelection = () => (
    <div className="flex-col-el items-center gap-6">
      {/* タイトル */}
      <p className="text-body text-textcolor-black text-[24px]">
        {I18n.t("MSG_PROFILE_AI_APPEARANCE_BY_PARENT")}
      </p>

      <div className="flex-col-el gap-4 w-full max-w-[600px]">
        {/* 身につけるもの（複数選択グリッド） */}
        <div className="flex-row-el items-center gap-4">
          <p className="text-body text-textcolor-black text-[20px] w-[180px] text-left">
            {I18n.t("MSG_ACCESSORY_TITLE_BY_PARENT")}
          </p>
          <div className="grid grid-cols-3 gap-3 w-[376px]">
            {ACCESSORY_OPTIONS.map((option) => {
              const isActive = (state.accessory || []).includes(option.key);
              return (
                <div
                  key={option.key}
                  onClick={() => handleAccessorySelect(option.key)}
                  className={`flex-col-el flex-center relative w-[120px] h-[120px] rounded-[8px] cursor-pointer select-none hover:opacity-90 ${isActive ? "bg-green-alt border-2 border-green-100" : "bg-surface border-2 border-gray2-40"}`}
                >
                  {isActive && (
                    <span className="absolute top-1 right-1 material-symbols-outlined text-[16px] text-green-100">
                      check_circle
                    </span>
                  )}
                  <span
                    className={`material-symbols-outlined !text-[32px] mb-1 ${isActive ? "text-green-100" : "text-gray2-80"}`}
                  >
                    {option.icon}
                  </span>
                  <span
                    className={`text-body text-[20px] text-center ${isActive ? "text-textcolor-black" : "text-textcolor-black/70"}`}
                  >
                    {I18n.t(option.labelKey)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* 髪型 */}
        <div className="flex-row-el items-center gap-4">
          <p className="text-body text-textcolor-black text-[20px] w-[180px] text-left">
            {I18n.t("MSG_HAIRSTYLE_TITLE_BY_PARENT")}
          </p>
          <div
            ref={hairstyleRef}
            onClick={() => {
              play();
              setHairstyleOpen(true);
            }}
            className="flex-row-el items-center justify-between w-[376px] h-[48px] px-4 bg-white border-2 border-gray2-60 rounded-[8px] cursor-pointer select-none hover:border-blue-100"
          >
            <span className="text-body text-[20px] text-textcolor-black">
              {getOptionLabel(HAIRSTYLE_OPTIONS, state.hairstyle)}
            </span>
            <span className="material-symbols-outlined !text-[20px]">expand_more</span>
          </div>
          <Select
            anchorRef={hairstyleRef}
            open={hairstyleOpen}
            onClose={() => setHairstyleOpen(false)}
            value={state.hairstyle}
            onChange={(v) => updateState({ hairstyle: v })}
            options={HAIRSTYLE_OPTIONS.map((o) => ({ ...o, label: I18n.t(o.label) }))}
          />
        </div>

        {/* 体型 */}
        <div className="flex-row-el items-center gap-4">
          <p className="text-body text-textcolor-black text-[20px] w-[180px] text-left">
            {I18n.t("MSG_BODY_TYPE_TITLE_BY_PARENT")}
          </p>
          <div
            ref={bodyTypeRef}
            onClick={() => {
              play();
              setBodyTypeOpen(true);
            }}
            className="flex-row-el items-center justify-between w-[376px] h-[48px] px-4 bg-white border-2 border-gray2-60 rounded-[8px] cursor-pointer select-none hover:border-blue-100"
          >
            <span className="text-body text-[20px] text-textcolor-black">
              {getOptionLabel(BODY_TYPE_OPTIONS, state.bodyType)}
            </span>
            <span className="material-symbols-outlined !text-[20px]">expand_more</span>
          </div>
          <Select
            anchorRef={bodyTypeRef}
            open={bodyTypeOpen}
            onClose={() => setBodyTypeOpen(false)}
            value={state.bodyType}
            onChange={(v) => updateState({ bodyType: v })}
            options={BODY_TYPE_OPTIONS.map((o) => ({ ...o, label: I18n.t(o.label) }))}
          />
        </div>

        {/* 顔の形 */}
        <div className="flex-row-el items-center gap-4">
          <p className="text-body text-textcolor-black text-[20px] w-[180px] text-left">
            {I18n.t("MSG_FACE_SHAPE_TITLE_BY_PARENT")}
          </p>
          <div
            ref={faceShapeRef}
            onClick={() => {
              play();
              setFaceShapeOpen(true);
            }}
            className="flex-row-el items-center justify-between w-[376px] h-[48px] px-4 bg-white border-2 border-gray2-60 rounded-[8px] cursor-pointer select-none hover:border-blue-100"
          >
            <span className="text-body text-[20px] text-textcolor-black">
              {getOptionLabel(FACE_SHAPE_OPTIONS, state.faceShape)}
            </span>
            <span className="material-symbols-outlined !text-[20px]">expand_more</span>
          </div>
          <Select
            anchorRef={faceShapeRef}
            open={faceShapeOpen}
            onClose={() => setFaceShapeOpen(false)}
            value={state.faceShape}
            onChange={(v) => updateState({ faceShape: v })}
            options={FACE_SHAPE_OPTIONS.map((o) => ({ ...o, label: I18n.t(o.label) }))}
          />
        </div>
      </div>
    </div>
  );

  /** サブステップに応じたコンテンツ */
  const renderContent = () => {
    switch (favoritesSubStep) {
      case "color":
        return renderColorSelection();
      case "bestSubject":
        return renderBestSubjectSelection();
      case "appearance":
        return renderAppearanceSelection();
      default:
        return null;
    }
  };

  return (
    <div className="flex-col-el items-center justify-center w-full h-full overflow-y-auto">
      {renderContent()}
    </div>
  );
};
